/**
 * The living dot field (beta only): what each dot looks like at a moment. At rest it is prod home's field, the text ink
 * on a 24 px grid, still. While Ivy works, violet rings ripple out from where she is. No dot sits behind the reading
 * column.
 */
export interface DotFieldOptions {
  spacing: number;
  /** The field's alpha at rest (the --b-dot token's). */
  alpha: number;
  motion?: boolean;
  working?: boolean;
  origin?: { x: number; y: number };
  /** The reading column: no dot inside it, a fade over `fade` px either side. */
  column?: { left: number; right: number; fade: number } | null;
  /** Fades the field out between these heights (the field belongs to the top of a page). */
  band?: { from: number; to: number } | null;
}

/** A dot's radius at rest (prod home's). */
export const DOT = 1.5;

const RING_SPEED = 180;
const RING_GAP = 300;
const RING_REACH = 900;
const RING_WIDTH = 48;

/** A dot's ink alpha, and how far a passing ring lights it violet (0 to 1). */
export function dotLook(x: number, y: number, t: number, o: DotFieldOptions): { a: number; lit: number } {
  let a = o.alpha;
  if (o.column) {
    const out = Math.max(o.column.left - x, x - o.column.right);
    a *= out >= o.column.fade ? 1 : Math.max(0, out / o.column.fade);
  }
  if (o.band) a *= y <= o.band.from ? 1 : y >= o.band.to ? 0 : 1 - (y - o.band.from) / (o.band.to - o.band.from);
  if (a <= 0) return { a: 0, lit: 0 };
  let lit = 0;
  if (o.motion && o.working && o.origin) {
    const dist = Math.hypot(x - o.origin.x, y - o.origin.y);
    const seen = a / o.alpha;
    for (let k = 0; k < 3; k++) {
      const ring = (t * RING_SPEED + k * RING_GAP) % RING_REACH;
      const near = 1 - Math.min(1, Math.abs(dist - ring) / RING_WIDTH);
      if (near > 0) lit = Math.max(lit, near * (1 - ring / RING_REACH) * seen);
    }
  }
  return { a, lit };
}
